<template>
  <div>
    <div
      class="el-select el-select--date"
      :class="[selectSize ? 'el-select--' + selectSize : '', { 'el-select--custom-label': showLabelWithDate}]"
      @click.stop="toggleMenu"
      v-clickoutside="handleClose"
    >
      <el-input
        ref="reference"
        v-model="selectedLabel"
        type="text"
        :autocomplete="autoComplete || autocomplete"
        :class="{ 'is-focus': visible }"
        :disabled="selectDisabled"
        :hidden="showLabelWithDate"
        :id="id"
        :name="name"
        :placeholder="currentPlaceholder"
        :readonly="readonly"
        :size="selectSize"
        :tabindex="(multiple && filterable) ? '-1' : null"
        :validate-event="false"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="debouncedOnInputChange"
        @keydown.native.down.stop.prevent="handleNavigate('next')"
        @keydown.native.up.stop.prevent="handleNavigate('prev')"
        @keydown.native.enter.prevent="selectOption"
        @keydown.native.esc.stop.prevent="visible = false"
        @keydown.native.tab="visible = false"
        @compositionstart="handleComposition"
        @compositionupdate="handleComposition"
        @compositionend="handleComposition"
        @mouseenter.native="inputHovering = true"
        @mouseleave.native="inputHovering = false"
      >
        <template
          slot="prepend"
          v-if="showLabelWithDate"
        >
          <div
            class="el-select__custom-label"
            slot="customLabel"
          >
            <template v-if="isCustomRange">
              <div class="date__wrapper">
                {{ selectedLabel }}
                <span class="date__text">({{ customRange[0] | formatDate(dateFormats.dateFns) }} - {{ customRange[1] | formatDate(dateFormats.dateFns) }})</span>
              </div>
              <i
                @click.stop="handleCalendarClick"
                class="calendar-icon icon icon-calendar px-4 ml-auto"
                v-if="!isMobile"
              />
            </template>

            <div
              class="date__wrapper"
              v-else
            >
              {{ selectedLabel }}
              <span class="date__text">{{ getFormatedDateRange(value) }}</span>
            </div>
          </div>
        </template>
        <template
          slot="prefix"
          v-if="$slots.prefix"
        >
          <slot name="prefix" />
        </template>
        <template slot="suffix">
          <i
            v-show="!showClose"
            :class="['el-select__caret', 'el-input__icon', 'el-icon-' + iconClass]"
          />
          <i
            v-if="showClose"
            class="el-select__caret el-input__icon el-icon-circle-close"
            @click="handleClearClick"
          />
        </template>
      </el-input>
      <transition
        name="el-zoom-in-top"
        @before-enter="handleMenuEnter"
        @after-leave="doDestroy"
      >
        <el-select-menu
          ref="popper"
          :append-to-body="popperAppendToBody"
          v-show="visible && emptyText !== false"
        >
          <el-scrollbar
            tag="ul"
            wrap-class="el-select-dropdown__wrap"
            view-class="el-select-dropdown__list"
            ref="scrollbar"
            :class="{ 'is-empty': !allowCreate && query && filteredOptionsCount === 0 }"
            v-show="options.length > 0 && !loading"
          >
            <el-option
              :value="query"
              created
              v-if="showNewOption"
            />
            <Option
              v-for="option in periods"
              :key="option"
              :value="option"
              :label="$t(`dateSelect.options.${option}`)"
            />
            <slot />
          </el-scrollbar>
          <template v-if="emptyText && (!allowCreate || loading || (allowCreate && options.length === 0 ))">
            <slot
              name="empty"
              v-if="$slots.empty"
            />
            <p
              class="el-select-dropdown__empty"
              v-else
            >
              {{ emptyText }}
            </p>
          </template>
        </el-select-menu>
      </transition>
    </div>

    <div
      class="d-flex align-items-center mt-24"
      v-if="isCustomRange && isMobile"
    >
      <DatePicker
        :clearable="false"
        :format="dateFormats.elementUi"
        v-model="customRange[0]"
        class="date-picker date-picker--small"
      />
      <span class="px-8">{{ dateFormats.rangeSeparator }}</span>
      <DatePicker
        :clearable="false"
        :format="dateFormats.elementUi"
        v-model="customRange[1]"
        class="date-picker date-picker--small"
      />
    </div>

    <DatePicker
      :append-to-body="true"
      :clearable="false"
      :format="dateFormats.elementUi"
      @change="handlePickerChange"
      class="date-picker date-picker--small date-picker--hidden"
      ref="dateRangePicker"
      type="daterange"
      v-if="isCustomRange && !isMobile"
      v-model="customRange"
    />
  </div>
</template>

<script>
import Select from '~/ui-library/Select.vue';
import { formatedDateRange } from '~/lib/formatedDateRange';
import { mapGetters } from 'vuex';

export default {
  name: 'DateSelect',
  extends: Select,
  props: {
    size: {
      type: String,
      default: '',
    },
    periods: {
      type: Array,
      default() {
        return [
          'all',
          'today',
          'yesterday',
          'this_month',
          'last_month',
          'this_year',
          'last_year',
          'last_12_months',
          'q1',
          'q2',
          'q3',
          'q4',
          'custom',
        ];
      },
    },
    customRangeFrom: {
      type: String,
      default: null,
    },
    customRangeTo: {
      type: String,
      default: null,
    },
    showLabelWithDate: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      customRange: [
        this.parseCustomDate(this.customRangeFrom),
        this.parseCustomDate(this.customRangeTo),
      ],
      isDatePickerOpen: false,
      isClosing: false,
    };
  },
  computed: {
    ...mapGetters('env', ['dateFormats']),
    isCustomRange() {
      return this.value === 'custom';
    },
  },
  methods: {
    getFormatedDateRange(period) {
      return formatedDateRange(period, this.dateFormats);
    },
    handleCalendarClick() {
      if (this.isDatePickerOpen) {
        this.$refs.dateRangePicker.blur();
      } else {
        this.$refs.dateRangePicker.focus();
      }

      this.isDatePickerOpen = !this.isDatePickerOpen;
    },
    handlePickerChange() {
      this.isDatePickerOpen = false;
    },
    parseCustomDate(dateString) {
      if (!dateString) return new Date();

      try {
        return new Date(dateString);
      } catch (e) {
        return new Date();
      }
    },
    resetCustomRange() {
      this.customRange = [new Date(), new Date()];
    },
  },
  watch: {
    customRange(value) {
      this.$emit('change-custom-range', value);
    },
  },
};
</script>
